/**
 * Video Player Module
 *
 * Handles automatic video playback control using Intersection Observer
 * - Sets all videos to autoplay=false by default
 * - Plays videos when they enter the viewport
 * - Pauses videos when they leave the viewport
 * - Monitors DOM for new video elements and applies the same behavior
 */

const createIntersectionObserver = () => {
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0.5
  };

  return new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const video = entry.target;

      if (entry.isIntersecting) {
        if (video.paused && !video.ended) {
          video.play().catch(error => {
            console.warn('Video playback was prevented:', error);
          });
        }
      } else {
        if (!video.paused) {
          video.pause();
        }
      }
    });
  }, options);
};

const prepareVideo = (video, observer) => {
  video.autoplay = false;
  observer.observe(video);
};

const setupExistingVideos = (observer) => {
  const videos = document.querySelectorAll('video');
  videos.forEach(video => prepareVideo(video, observer));
};

const createMutationObserver = (intersectionObserver) => {
  const mutationObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node instanceof HTMLElement) {
          if (node instanceof HTMLVideoElement) {
            prepareVideo(node, intersectionObserver);
          }
          const nestedVideos = node.querySelectorAll('video');
          nestedVideos.forEach(video => {
            prepareVideo(video, intersectionObserver);
          });
        }
      });
    });
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: false,
    characterData: false
  });

  return mutationObserver;
};


const init = () => {
  const videoObserver = createIntersectionObserver();

  setupExistingVideos(videoObserver);

  const domObserver = createMutationObserver(videoObserver);

  return () => {
      videoObserver.disconnect();
      domObserver.disconnect();
  };
};

// Export the initialization function
export default { init };
